import type {
  ConcreteObjectiveTerm,
  ObjectiveTermOperation,
  OperationTerm,
} from '@moment/horizon-data-access/proposal'

import { generateId } from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'

import {
  type EquationToken,
  getOperatorPrecedence,
  type Operator,
  type TermToken,
} from './equation-tokens'

// Bidirectional operator <-> operation mapping

const OPERATOR_TO_OPERATION: Record<Operator, ObjectiveTermOperation> = {
  '+': 'add',
  '-': 'subtract',
  '*': 'multiply',
  '/': 'divide',
}

const OPERATION_TO_OPERATOR: Partial<Record<ObjectiveTermOperation, Operator>> =
  {
    add: '+',
    subtract: '-',
    multiply: '*',
    divide: '/',
  }

export function operatorToOperation(op: Operator): ObjectiveTermOperation {
  return OPERATOR_TO_OPERATION[op]
}

export function operationToOperator(
  operation: ObjectiveTermOperation,
): Operator | null {
  return OPERATION_TO_OPERATOR[operation] ?? null
}

// Tokens -> API tree (shunting-yard)

type PostfixItem =
  | { type: 'term'; token: TermToken }
  | { type: 'operator'; op: Operator }

export function tokensToObjectiveTree(
  tokens: EquationToken[],
): ConcreteObjectiveTerm | undefined {
  if (tokens.length === 0) return undefined

  const output: PostfixItem[] = []
  const opStack: (Operator | '(')[] = []

  for (const token of tokens) {
    switch (token.type) {
      case 'term':
        output.push({ type: 'term', token })
        break
      case 'operator': {
        const prec = getOperatorPrecedence(token.op)
        while (opStack.length > 0) {
          const top = opStack[opStack.length - 1]
          if (top === '(') break
          // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          if (getOperatorPrecedence(top!) >= prec) {
            output.push({ type: 'operator', op: opStack.pop() as Operator })
          } else {
            break
          }
        }
        opStack.push(token.op)
        break
      }
      case 'paren':
        if (token.side === 'open') {
          opStack.push('(')
        } else {
          while (opStack.length > 0 && opStack[opStack.length - 1] !== '(') {
            output.push({ type: 'operator', op: opStack.pop() as Operator })
          }
          opStack.pop() // remove '('
        }
        break
    }
  }

  // Flush remaining operators, skipping unmatched '('
  while (opStack.length > 0) {
    const item = opStack.pop()
    if (!item || item === '(') continue
    output.push({ type: 'operator', op: item })
  }

  // Build tree from postfix
  const stack: ConcreteObjectiveTerm[] = []
  for (const item of output) {
    if (item.type === 'term') {
      const { id: _id, type: _type, ...termData } = item.token
      stack.push({ type: 'field', ...termData })
    } else {
      const right = stack.pop()
      const left = stack.pop()
      if (left && right) {
        const opTerm: OperationTerm = {
          type: 'operation',
          operation: operatorToOperation(item.op),
          left,
          right,
        }
        stack.push(opTerm)
      } else if (left) {
        // Malformed: missing right operand, push left back
        stack.push(left)
      } else if (right) {
        // Malformed: missing left operand, push right back
        stack.push(right)
      }
    }
  }

  return stack.length === 1 ? stack[0] : undefined
}

// API tree -> tokens (in-order walk)

function needsParens(
  parentOp: Operator,
  childOp: Operator,
  isRightOperand: boolean,
): boolean {
  const parentPrec = getOperatorPrecedence(parentOp)
  const childPrec = getOperatorPrecedence(childOp)
  // Lower precedence child always needs parens
  if (childPrec < parentPrec) return true
  // Same precedence on right side of - or / needs parens for correctness
  if (childPrec === parentPrec && isRightOperand) {
    return parentOp === '-' || parentOp === '/'
  }
  return false
}

export function objectiveTreeToTokens(tree: ConcreteObjectiveTerm): {
  tokens: EquationToken[]
} {
  const tokens: EquationToken[] = []

  function walk(
    node: ConcreteObjectiveTerm,
    parentOp: Operator | null,
    isRightOperand: boolean,
  ) {
    if (node.type === 'field') {
      const termToken: TermToken = {
        type: 'term',
        id: generateId(),
        field: node.field,
        ...(node.factor !== undefined && { factor: node.factor }),
        // FilterDictionary and the editable token's filter describe the same
        // wire format; narrow it back for the token here.
        ...(node.filter && {
          filter: node.filter as TermToken['filter'],
        }),
      }
      tokens.push(termToken)
      return
    }

    if (node.type === 'operation') {
      const op = operationToOperator(node.operation)
      if (!op) {
        // Unsupported operation (max, min) — flatten both operands with + between
        walk(node.left, parentOp, isRightOperand)
        tokens.push({ type: 'operator', op: '+' })
        walk(node.right, parentOp, isRightOperand)
        return
      }

      const wrap =
        parentOp !== null && needsParens(parentOp, op, isRightOperand)

      if (wrap) tokens.push({ type: 'paren', side: 'open' })
      walk(node.left, op, false)
      tokens.push({ type: 'operator', op })
      walk(node.right, op, true)
      if (wrap) tokens.push({ type: 'paren', side: 'close' })
    }
  }

  walk(tree, null, false)
  return { tokens }
}
